/**
 * pricing.gql-map.ts — the PURE mapping layer between the pricing graph
 * shapes and the legacy REST types (CU-1b; taxonomy.gql-map precedent).
 *
 * Deliberately import-free of the axios/i18n stack so the vitest matrix can
 * exercise every derivation in the plain node environment.
 *
 * Three stances, each the reason a whole class of drift cannot happen:
 *
 * 1. MONEY IS A STRING ON THE WIRE (ADR-008). The graph never emits a float
 *    for an amount; this file is the ONE place a `Money` becomes the `number`
 *    the legacy REST types declare. `money()` refuses a non-numeric string
 *    rather than yielding NaN downstream — a price that silently became NaN
 *    is the worst failure this module could have.
 *
 * 2. ENUMS COME BACK THROUGH A TABLE, NEVER `toLowerCase()`. The legacy
 *    vocabulary is not mechanically derivable from SCREAMING_SNAKE in every
 *    case, and a mechanical transform would happily invent a legacy member
 *    that does not exist. Each table is exhaustive over the SDL enum; an
 *    unrecognised member throws with the field named.
 *
 * 3. KEY PRESENCE IS PART OF THE CONTRACT (the CU-1a bank-wire lesson). The
 *    mock's rows omit keys whose value was never set — a `markup_pct` special
 *    price has NO `fixedPrice`/`currency` keys at all, a product-pricing row
 *    with no ad-hoc brackets has no `quantityBrackets` key. The graph answers
 *    `null` / `[]` for the same state, so every optional member here is
 *    emitted ONLY when it carries a value. Emitting `null` instead would be a
 *    byte-diff on every row of the shadow leg, and in the UI would turn
 *    "never entered" into "explicitly empty".
 */

import type {
  GqlCostRecord,
  GqlCurrencyPriceRow,
  GqlCustomerGroupDiscount,
  GqlCustomerPriceAssignment,
  GqlExchangeRate,
  GqlMarkupRule,
  GqlPriceDiscountTraceEntry,
  GqlPriceGroupBand,
  GqlPriceLevel,
  GqlPriceResolution,
  GqlPriceTraceEntry,
  GqlPriceWaterfallRow,
  GqlPricingGroup,
  GqlProductPricingFields,
  GqlQuantityBracket,
  GqlQuantityPricingSchedule,
  GqlSpecialItemPrice,
} from './pricing.operations';
import type {
  CostBasis,
  CostEstimateType,
  CostRecord,
  CurrencyPriceRow,
  CustomerCategoryPriceLevel,
  CustomerGroupDiscount,
  CustomerPricing,
  ExchangeRate,
  FxSnapshot,
  LandedCostAllocationBasis,
  MarkupRule,
  PriceCurrencySkipReason,
  PriceDiscountSkipReason,
  PriceDiscountStage,
  PriceDiscountTraceEntry,
  PriceGroupBand,
  PriceGroupCode,
  PriceLevel,
  PriceResolution,
  PriceSkipReason,
  PriceSource,
  PriceTraceEntry,
  PriceWaterfallKey,
  PriceWaterfallRow,
  PriceWinner,
  PricingAnchor,
  PricingGroup,
  PricingTier,
  ProductPricingFields,
  QuantityBracket,
  QuantityDiscountBasis,
  QuantityPricingSchedule,
  SpecialItemPrice,
} from '@/types/api/pricing';

// ---------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------

/**
 * `Money` (canonical decimal string) → the legacy `number`. Never NaN, and —
 * the case the first version missed — never a SILENT ZERO.
 *
 * `Number('')` and `Number('   ')` are both `0`, not `NaN`, so a blank amount
 * on the wire would have parsed to a perfectly finite zero and shipped as a
 * price. The seam vitest caught it before any corpus produced one. The blank
 * check therefore comes FIRST and is not folded into the finite check.
 */
export function money(value: string): number {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error('pricing.gql-map: Money is blank — a blank amount is not a finite number');
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    throw new Error(`pricing.gql-map: Money "${value}" is not a finite number`);
  }
  return parsed;
}

/** Nullable `Money` → `number | null`. */
export function moneyOrNull(value: string | null): number | null {
  return value === null ? null : money(value);
}

function lookup<T extends string>(
  table: Readonly<Record<string, T>>,
  member: string,
  field: string
): T {
  const mapped = table[member];
  if (mapped === undefined) {
    throw new Error(`pricing.gql-map: unknown ${field} member "${member}"`);
  }
  return mapped;
}

// ---------------------------------------------------------------------------
// Enum tables — exhaustive over the frozen SDL enums
// ---------------------------------------------------------------------------

const PRICE_SOURCE: Readonly<Record<string, PriceSource>> = { CH: 'CH', TW: 'TW' };
const PRICE_GROUP_CODE: Readonly<Record<string, PriceGroupCode>> = {
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
};
const SPECIAL_KIND: Readonly<Record<string, 'fixed' | 'markup_pct'>> = {
  FIXED: 'fixed',
  MARKUP_PCT: 'markup_pct',
};
const COST_BASIS: Readonly<Record<string, CostBasis>> = {
  ACTUAL: 'actual',
  ESTIMATED: 'estimated',
};
const ALLOCATION_BASIS: Readonly<Record<string, LandedCostAllocationBasis>> = {
  QUANTITY: 'quantity',
  VALUE: 'value',
  WEIGHT: 'weight',
};
const QUANTITY_BASIS: Readonly<Record<string, QuantityDiscountBasis>> = {
  BY_LINE_QUANTITY: 'by_line_quantity',
};
const GROUP_DISCOUNT_KIND: Readonly<Record<string, 'discount_pct' | 'price_level'>> = {
  DISCOUNT_PCT: 'discount_pct',
  PRICE_LEVEL: 'price_level',
};
const COST_ESTIMATE_TYPE: Readonly<Record<string, CostEstimateType>> = {
  AVERAGE_COST: 'average_cost',
  ITEM_DEFINED_COST: 'item_defined_cost',
  LAST_PURCHASE_PRICE: 'last_purchase_price',
  PURCHASE_PRICE: 'purchase_price',
};
const PRICING_TIER: Readonly<Record<string, PricingTier>> = {
  SPECIAL_OVERRIDE: 'special_override',
  CUSTOMER_LEVEL: 'customer_level',
  LIST_PRICE: 'list_price',
};
const PRICE_WINNER: Readonly<Record<string, PriceWinner>> = {
  ...PRICING_TIER,
  UNRESOLVED: 'unresolved',
};
const SKIP_REASON: Readonly<Record<string, PriceSkipReason>> = {
  NO_CUSTOMER: 'no_customer',
  NO_RULE_FOUND: 'no_rule_found',
  OUTSIDE_VALIDITY: 'outside_validity',
  INACTIVE: 'inactive',
  NO_PRICE_LEVEL_ASSIGNED: 'no_price_level_assigned',
  PRICE_LEVEL_INACTIVE: 'price_level_inactive',
  NO_BAND_MATCH: 'no_band_match',
  NO_MARKUP_RULE: 'no_markup_rule',
  NO_COST_RECORD: 'no_cost_record',
  LOWER_PRECEDENCE: 'lower_precedence',
};
const DISCOUNT_STAGE: Readonly<Record<string, PriceDiscountStage>> = {
  GROUP_DISCOUNT: 'group_discount',
  QUANTITY_DISCOUNT: 'quantity_discount',
};
const DISCOUNT_SKIP_REASON: Readonly<Record<string, PriceDiscountSkipReason>> = {
  NO_CUSTOMER: 'no_customer',
  NO_GROUP: 'no_group',
  NO_RULE_FOUND: 'no_rule_found',
  NO_QUANTITY: 'no_quantity',
  NO_DETERMINED_PRICE: 'no_determined_price',
  FIXED_PRICE_LOCKED: 'fixed_price_locked',
  SPECIAL_OVERRIDE_FINAL: 'special_override_final',
  // The two quantity-schedule refusals develop's 適用期間 / group-card work
  // added (FREEZE amendment 6, 2026-09-16: SDL enum additions, `dangerous`,
  // never breaking). Every other layer already carries them —
  // `resolvePrice.ts` EMITS both (`customer_scope`, `outside_apply_period`),
  // the legacy union declares both, and the server's Pothos enum exposes both
  // — so this table was the only layer short of the vocabulary, and a live
  // trace carrying either member would have thrown at the seam.
  //
  // They are deliberately NOT laundered onto `no_rule_found`: that is the
  // exact false trace the SDL description says this vocabulary exists to
  // prevent — a configured schedule that simply did not apply today must not
  // read as a schedule that does not exist.
  OUTSIDE_APPLY_PERIOD: 'outside_apply_period',
  CUSTOMER_SCOPE: 'customer_scope',
};
const WATERFALL_KEY: Readonly<Record<string, PriceWaterfallKey>> = {
  PURCHASE_COST: 'purchase_cost',
  LANDED_ALLOCATION: 'landed_allocation',
  LANDED_COST: 'landed_cost',
  INTERNAL_MARKUP: 'internal_markup',
  PRICING_BASE: 'pricing_base',
  SALES_MARKUP: 'sales_markup',
  LIST_PRICE: 'list_price',
  LEVEL_MARKUP: 'level_markup',
  CUSTOMER_DISCOUNT: 'customer_discount',
  SPECIAL_FIXED: 'special_fixed',
  SPECIAL_OVERRIDE_PCT: 'special_override_pct',
  CURRENCY_LOCKED_PRICE: 'currency_locked_price',
  GROUP_DISCOUNT: 'group_discount',
  QUANTITY_DISCOUNT: 'quantity_discount',
};
const CURRENCY_SKIP_REASON: Readonly<Record<string, PriceCurrencySkipReason>> = {
  NO_CURRENCY_PRICE: 'no_currency_price',
};
const PRICING_ANCHOR: Readonly<Record<string, PricingAnchor>> = {
  PURCHASE_COST: 'purchase_cost',
  LANDED_COST: 'landed_cost',
  PRICING_BASE: 'pricing_base',
};

/** The write direction for the two enums a mutation input carries. */
export const SPECIAL_KIND_TO_GQL: Readonly<Record<string, string>> = {
  fixed: 'FIXED',
  markup_pct: 'MARKUP_PCT',
};
/**
 * The write direction for `PricingAnchor` — the mirror of `PRICING_ANCHOR`
 * above, which only ever read. Its absence is why `saveSpecialPrice` could not
 * send `basisAtAuthoring`: the read side has mapped the enum since the anchor
 * landed, the write side had no map to reach for (2026-09-16).
 */
export const PRICING_ANCHOR_TO_GQL: Readonly<Record<string, string>> = {
  purchase_cost: 'PURCHASE_COST',
  landed_cost: 'LANDED_COST',
  pricing_base: 'PRICING_BASE',
};
export const GROUP_DISCOUNT_KIND_TO_GQL: Readonly<Record<string, string>> = {
  discount_pct: 'DISCOUNT_PCT',
  price_level: 'PRICE_LEVEL',
};
export const COST_BASIS_TO_GQL: Readonly<Record<string, string>> = {
  actual: 'ACTUAL',
  estimated: 'ESTIMATED',
};
export const ALLOCATION_BASIS_TO_GQL: Readonly<Record<string, string>> = {
  quantity: 'QUANTITY',
  value: 'VALUE',
  weight: 'WEIGHT',
};
export const QUANTITY_BASIS_TO_GQL: Readonly<Record<string, string>> = {
  by_line_quantity: 'BY_LINE_QUANTITY',
};
export const COST_ESTIMATE_TYPE_TO_GQL: Readonly<Record<string, string>> = {
  average_cost: 'AVERAGE_COST',
  item_defined_cost: 'ITEM_DEFINED_COST',
  last_purchase_price: 'LAST_PURCHASE_PRICE',
  purchase_price: 'PURCHASE_PRICE',
};

// ---------------------------------------------------------------------------
// Collections
// ---------------------------------------------------------------------------

export function mapPriceLevel(row: GqlPriceLevel): PriceLevel {
  return {
    id: row.id,
    code: row.code,
    nameEn: row.nameEn,
    nameZh: row.nameZh,
    sortOrder: row.sortOrder,
    isActive: row.isActive,
  };
}

export function mapPriceGroupBand(row: GqlPriceGroupBand): PriceGroupBand {
  return {
    id: row.id,
    group: lookup(PRICE_GROUP_CODE, row.group, 'PriceGroupCode'),
    minInclusive: money(row.minInclusive),
    maxExclusive: moneyOrNull(row.maxExclusive),
    currency: row.currency,
    // The wire field is TOTAL (`Boolean!`); the legacy type's is OPTIONAL with
    // ABSENT ⇒ ACTIVE. Mapping `true` back to ABSENCE is the presence rule this
    // seam already applies everywhere (`pricingSeam` claim 4) and it is what
    // keeps an active band byte-identical to its `mockPriceGroupBands` twin —
    // the pinned field-for-field band assertions read the object literally.
    ...(row.isActive ? {} : { isActive: false }),
  };
}

export function mapMarkupRule(row: GqlMarkupRule): MarkupRule {
  return {
    id: row.id,
    priceGroupId: row.priceGroupId,
    source: lookup(PRICE_SOURCE, row.source, 'PriceSource'),
    priceLevelId: row.priceLevelId,
    markupPct: row.markupPct,
  };
}

/**
 * Rebuild the FE's DISCRIMINATED UNION. The graph carries both variants'
 * members on one type (a union of input-object variants is not expressible in
 * GraphQL), so `kind` decides which members exist on the legacy row — the
 * inactive variant's keys are ABSENT, exactly as the mock stores them.
 *
 * `basisAtAuthoring` travels with `markupPct`: both are members of the markup
 * arm, both are required there, and a row missing either is refused rather than
 * completed with a guess.
 */
export function mapSpecialItemPrice(row: GqlSpecialItemPrice): SpecialItemPrice {
  const base = {
    id: row.id,
    customerId: row.customerId,
    productId: row.productId,
    reason: row.reason,
    validFrom: row.validFrom,
    validTo: row.validTo,
    isActive: row.isActive,
    createdBy: row.createdBy,
    createdAt: row.createdAt,
  };
  const kind = lookup(SPECIAL_KIND, row.kind, 'SpecialItemPriceKind');
  if (kind === 'fixed') {
    if (row.fixedPrice === null || row.currency === null) {
      throw new Error(`pricing.gql-map: FIXED special price ${row.id} without price/currency`);
    }
    return { ...base, kind, fixedPrice: money(row.fixedPrice), currency: row.currency };
  }
  if (row.markupPct === null) {
    throw new Error(`pricing.gql-map: MARKUP_PCT special price ${row.id} without markupPct`);
  }
  // `basisAtAuthoring` is a MEMBER of the markup arm, exactly like `markupPct`,
  // so its absence is refused the same way. Defaulting to `pricing_base` would
  // be worse than a throw: it invents a regime, and a row whose regime was
  // invented resolves off a figure it was never priced against.
  if (row.basisAtAuthoring === null) {
    throw new Error(
      `pricing.gql-map: MARKUP_PCT special price ${row.id} without basisAtAuthoring`
    );
  }
  return {
    ...base,
    kind,
    markupPct: row.markupPct,
    basisAtAuthoring: lookup(PRICING_ANCHOR, row.basisAtAuthoring, 'PricingAnchor'),
  };
}

export function mapCustomerPricing(row: GqlCustomerPriceAssignment): CustomerPricing {
  const mapped: CustomerPricing = {
    customerId: row.customerId,
    priceLevelId: row.priceLevelId,
  };
  if (row.discountPct !== null) mapped.discountPct = row.discountPct;
  if (row.perCategoryPriceLevels.length > 0) {
    mapped.perCategoryPriceLevels = row.perCategoryPriceLevels.map(
      (entry): CustomerCategoryPriceLevel => ({
        categoryId: entry.categoryId,
        priceLevelId: entry.priceLevelId,
      })
    );
  }
  return mapped;
}

export function mapCostRecord(row: GqlCostRecord): CostRecord {
  const mapped: CostRecord = {
    id: row.id,
    productId: row.productId,
    source: lookup(PRICE_SOURCE, row.source, 'PriceSource'),
    basis: lookup(COST_BASIS, row.basis, 'CostBasis'),
    purchaseCost: money(row.purchaseCost),
    landedAllocation: money(row.landedAllocation),
    internalMarkupPct: row.internalMarkupPct,
    salesMarkupPct: row.salesMarkupPct,
    currency: row.currency,
    fxSnapshot: row.fxSnapshot === null ? null : mapFxSnapshot(row.fxSnapshot),
    effectiveDate: row.effectiveDate,
  };
  // `trackLandedCost` is non-null on the graph but OPTIONAL on the legacy row;
  // the mock omits it when the product never opted in.
  if (row.trackLandedCost) mapped.trackLandedCost = true;
  if (row.landedCostAllocationBasis !== null) {
    mapped.landedCostAllocationBasis = lookup(
      ALLOCATION_BASIS,
      row.landedCostAllocationBasis,
      'LandedCostAllocationBasis'
    );
  }
  return mapped;
}

/**
 * `capturedAt` crosses as a `DateTime` (ISO-8601 with offset) while the stored
 * fixture writes `2026-06-01T00:00:00Z`. Both worlds normalise timestamps to
 * `<ts>` in the shadow transcript, so the value is compared for PRESENCE and
 * the display layer parses either spelling.
 */
export function mapFxSnapshot(row: {
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  capturedAt: string;
}): FxSnapshot {
  return {
    fromCurrency: row.fromCurrency,
    toCurrency: row.toCurrency,
    rate: row.rate,
    capturedAt: row.capturedAt,
  };
}

export function mapQuantityBracket(row: GqlQuantityBracket): QuantityBracket {
  return { minQuantity: row.minQuantity, discountPct: row.discountPct };
}

export function mapQuantitySchedule(row: GqlQuantityPricingSchedule): QuantityPricingSchedule {
  return {
    id: row.id,
    code: row.code,
    nameEn: row.nameEn,
    nameZh: row.nameZh,
    basis: lookup(QUANTITY_BASIS, row.basis, 'QuantityDiscountBasis'),
    brackets: row.brackets.map(mapQuantityBracket),
    isActive: row.isActive,
  };
}

export function mapPricingGroup(row: GqlPricingGroup): PricingGroup {
  return {
    id: row.id,
    code: row.code,
    nameEn: row.nameEn,
    nameZh: row.nameZh,
    // ABSENT, never `null`, when the graph answered null — the seam's standing
    // presence rule. For `defaultDiscountPct` that is load-bearing: `null` is
    // "no standard rate" and `0` is "a deliberate 0%", and `?? undefined` on a
    // 0 would be the same collapse `pricingGroupCreatePayload` pins against.
    ...(row.categoryId === null ? {} : { categoryId: row.categoryId }),
    ...(row.defaultDiscountPct === null
      ? {}
      : { defaultDiscountPct: row.defaultDiscountPct }),
    isActive: row.isActive,
  };
}

export function mapGroupDiscount(row: GqlCustomerGroupDiscount): CustomerGroupDiscount {
  const base = { id: row.id, customerId: row.customerId, groupId: row.groupId };
  const kind = lookup(GROUP_DISCOUNT_KIND, row.kind, 'CustomerGroupDiscountKind');
  if (kind === 'discount_pct') {
    if (row.discountPct === null) {
      throw new Error(`pricing.gql-map: DISCOUNT_PCT card ${row.id} without discountPct`);
    }
    return { ...base, kind, discountPct: row.discountPct };
  }
  if (row.priceLevelId === null) {
    throw new Error(`pricing.gql-map: PRICE_LEVEL card ${row.id} without priceLevelId`);
  }
  return { ...base, kind, priceLevelId: row.priceLevelId };
}

export function mapCurrencyPriceRow(row: GqlCurrencyPriceRow): CurrencyPriceRow {
  return {
    id: row.id,
    productId: row.productId,
    priceLevelId: row.priceLevelId,
    currency: row.currency,
    price: money(row.price),
    fixedPrice: row.fixedPrice,
  };
}

export function mapProductPricingFields(row: GqlProductPricingFields): ProductPricingFields {
  const mapped: ProductPricingFields = { productId: row.productId };
  if (row.pricingGroupId !== null) mapped.pricingGroupId = row.pricingGroupId;
  if (row.quantityScheduleId !== null) mapped.quantityScheduleId = row.quantityScheduleId;
  if (row.costEstimateType !== null) {
    mapped.costEstimateType = lookup(COST_ESTIMATE_TYPE, row.costEstimateType, 'CostEstimateType');
  }
  if (row.quantityBrackets.length > 0) {
    mapped.quantityBrackets = row.quantityBrackets.map(mapQuantityBracket);
  }
  return mapped;
}

export function mapExchangeRate(row: GqlExchangeRate): ExchangeRate {
  return {
    fromCurrency: row.fromCurrency,
    toCurrency: row.toCurrency,
    rate: row.rate,
    effectiveDate: row.effectiveDate,
  };
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

export function mapTraceEntry(row: GqlPriceTraceEntry): PriceTraceEntry {
  const mapped: PriceTraceEntry = {
    tier: lookup(PRICING_TIER, row.tier, 'PricingTier'),
    matched: row.matched,
    won: row.won,
  };
  if (row.candidatePrice !== null) mapped.candidatePrice = money(row.candidatePrice);
  if (row.ruleRef !== null) mapped.ruleRef = row.ruleRef;
  if (row.skipReason !== null) {
    mapped.skipReason = lookup(SKIP_REASON, row.skipReason, 'PriceSkipReason');
  }
  return mapped;
}

export function mapDiscountTraceEntry(
  row: GqlPriceDiscountTraceEntry
): PriceDiscountTraceEntry {
  const mapped: PriceDiscountTraceEntry = {
    stage: lookup(DISCOUNT_STAGE, row.stage, 'PriceDiscountStage'),
    matched: row.matched,
  };
  if (row.candidatePrice !== null) mapped.candidatePrice = money(row.candidatePrice);
  if (row.discountPct !== null) mapped.discountPct = row.discountPct;
  if (row.ruleRef !== null) mapped.ruleRef = row.ruleRef;
  if (row.skipReason !== null) {
    mapped.skipReason = lookup(
      DISCOUNT_SKIP_REASON,
      row.skipReason,
      'PriceDiscountSkipReason'
    );
  }
  return mapped;
}

export function mapWaterfallRow(row: GqlPriceWaterfallRow): PriceWaterfallRow {
  return {
    key: lookup(WATERFALL_KEY, row.key, 'PriceWaterfallKey'),
    labelKey: row.labelKey,
    formula: row.formula,
    amount: money(row.amount),
  };
}

/**
 * The whole point of the pricing flip: this object must equal, byte for byte,
 * what `resolvePrice` returns on the mock seam — because it IS what
 * `resolvePrice` returned, having crossed a wire. Optional members follow the
 * mock's key-presence contract: `discountTrace` only when the dataset carried
 * product pricing, `currencySkipReason` only on a currency-mode refusal.
 */
export function mapPriceResolution(row: GqlPriceResolution): PriceResolution {
  const mapped: PriceResolution = {
    unitPrice: moneyOrNull(row.unitPrice),
    currency: row.currency,
    // Key ORDER mirrors the domain literal `resolvePrice` returns (anchor
    // before winner), so a stringified round trip stays byte-identical.
    pricingAnchor: lookup(PRICING_ANCHOR, row.pricingAnchor, 'PricingAnchor'),
    winner: lookup(PRICE_WINNER, row.winner, 'PriceWinner'),
    trace: row.trace.map(mapTraceEntry),
    waterfall: row.waterfall.map(mapWaterfallRow),
    marginVsLanded:
      row.marginVsLanded === null
        ? null
        : { amount: money(row.marginVsLanded.amount), pct: row.marginVsLanded.pct },
    asOf: row.asOf,
  };
  if (row.discountTrace !== null) {
    mapped.discountTrace = row.discountTrace.map(mapDiscountTraceEntry);
  }
  if (row.currencySkipReason !== null) {
    mapped.currencySkipReason = lookup(
      CURRENCY_SKIP_REASON,
      row.currencySkipReason,
      'PriceCurrencySkipReason'
    );
  }
  return mapped;
}
